import React, { useState, useEffect } from "react";
import { Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import { api, track } from "@/lib/api";
import { toast } from "sonner";

const EVENT_TYPES = ["Wedding", "Birthday Party", "Private Dinner", "Corporate Event", "Family Gathering", "Rehearsal Dinner", "Festival", "Community Event", "Holiday Party", "Special Celebration"];
const SERVICE = ["Buffet", "Plated", "Food Truck / Drop-off"];
const BUDGETS = ["Under $500", "$500 – $1,500", "$1,500 – $3,000", "$3,000 – $5,000", "$5,000+"];

export default function CateringPage() {
  const { settings } = useData();
  const [f, setF] = useState({ name: "", email: "", phone: "", event_date: "", event_type: "", location: "", guests: 40, service_type: "Buffet", preferred_menu: "", budget: "", details: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const packages = settings?.catering_packages || [];
  const faqs = (settings?.faqs || []).filter((x) => x.q);

  useEffect(() => {
    const withA = faqs.filter((x) => x.a);
    if (!withA.length) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: withA.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
    });
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
    // eslint-disable-next-line
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) return toast.error("Name, email and phone are required");
    setLoading(true);
    try {
      await api.post("/catering", { ...f, guests: Number(f.guests) });
      track("catering_quote_submit", { event_type: f.event_type, guests: Number(f.guests) });
      setSent(true);
      toast.success("Catering request sent! We'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please call us.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative py-24 bg-gradient-to-b from-emerald-950 to-[#101311] tropical-grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400"><Sparkles className="w-4 h-4" /> Catering</span>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl mt-3">Bring Sir. King Kitchen To Your Event</h1>
          <p className="text-[#FDFBF7]/75 mt-5 text-lg">Caribbean flavor for weddings, corporate events, private dinners and every celebration. {settings?.service_area_note || "Serving all of New England"}. {settings?.booking_note || "Now booking events."}</p>
          <div className="flex flex-wrap justify-center gap-2 mt-7">
            {EVENT_TYPES.map((t) => <span key={t} className="px-3.5 py-1.5 rounded-full border border-emerald-700/50 text-[#FDFBF7]/80 text-sm">{t}</span>)}
          </div>
        </div>
      </section>

      {packages.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Catering Packages</span>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-2">Ways We Cater</h2>
            <p className="text-[#FDFBF7]/60 text-sm mt-2">Every event is custom — these are starting points. Pricing confirmed in your quote.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {packages.map((p, i) => (
              <div key={`${p.name}-${i}`} data-testid={`catering-package-${i}`} className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 flex flex-col">
                <h3 className="font-display font-black text-xl">{p.name}</h3>
                <div className="text-amber-400 font-bold mt-1">{p.price ? p.price : "Custom quote"} <span className="text-[#FDFBF7]/50 font-normal text-sm">· {p.min_guests} guests</span></div>
                <p className="text-sm text-[#FDFBF7]/70 mt-3 flex-1">{p.includes}</p>
                {p.note && <p className="text-xs text-amber-300/80 mt-3">{p.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {sent ? (
          <div className="rounded-3xl bg-[#181D1A] border border-amber-500/40 p-10 text-center gold-glow">
            <CheckCircle2 className="w-14 h-14 text-amber-400 mx-auto" />
            <h2 className="font-display font-black text-3xl uppercase mt-4">Request Received!</h2>
            <p className="text-[#FDFBF7]/75 mt-3">Thanks {f.name}! Our team will reach out to craft your custom Caribbean menu. For anything urgent, call {settings?.phone || "207-773-8000"}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 sm:p-8 space-y-4">
            <h2 className="font-display font-black text-2xl uppercase text-amber-400">Request A Catering Quote</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *"><input data-testid="catering-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
              <Field label="Phone *"><input data-testid="catering-phone" type="tel" autoComplete="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
              <Field label="Email *"><input data-testid="catering-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} /></Field>
              <Field label="Event Date"><input data-testid="catering-date" type="date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} className={inp} /></Field>
              <Field label="Event Type"><select data-testid="catering-type" value={f.event_type} onChange={(e) => set("event_type", e.target.value)} className={inp}><option value="">Select…</option>{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Location"><input data-testid="catering-location" value={f.location} onChange={(e) => set("location", e.target.value)} className={inp} placeholder="City / venue" /></Field>
              <Field label="Service Type"><select data-testid="catering-service" value={f.service_type} onChange={(e) => set("service_type", e.target.value)} className={inp}>{SERVICE.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Budget Range"><select data-testid="catering-budget" value={f.budget} onChange={(e) => set("budget", e.target.value)} className={inp}><option value="">Select…</option>{BUDGETS.map((t) => <option key={t}>{t}</option>)}</select></Field>
            </div>
            <Field label={`Number of Guests: ${f.guests}`}>
              <input data-testid="catering-guests" type="range" min="10" max="300" step="5" value={f.guests} onChange={(e) => set("guests", e.target.value)} className="w-full accent-amber-400" />
            </Field>
            <Field label="Preferred Menu"><input data-testid="catering-menu" value={f.preferred_menu} onChange={(e) => set("preferred_menu", e.target.value)} className={inp} placeholder="Jerk chicken, oxtails, rasta pasta…" /></Field>
            <Field label="Additional Details"><textarea data-testid="catering-details" rows={4} value={f.details} onChange={(e) => set("details", e.target.value)} className={inp} placeholder="Tell us about your event…" /></Field>
            <button data-testid="catering-form-submit-button" disabled={loading} className="w-full py-4 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Request A Catering Quote"}
            </button>
          </form>
        )}
      </section>
      {faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl text-center mb-6">Catering FAQs</h2>
          <div className="space-y-3">
            {faqs.map((x, i) => (
              <details key={x.q || i} data-testid={`faq-${i}`} className="rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-4">
                <summary className="font-display font-bold cursor-pointer list-none flex justify-between items-center">{x.q}<span className="text-amber-400">+</span></summary>
                <p className="text-[#FDFBF7]/70 text-sm mt-2">{x.a || `Call us at ${settings?.phone || "207-773-8000"} for details.`}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const inp = "w-full bg-[#101311] rounded-xl px-4 py-3 text-sm border border-emerald-800/50 outline-none focus:border-amber-400";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-amber-400 font-bold mb-1.5">{label}</span>
    {children}
  </label>
);
